/**
 * 图生视频参考图上传器
 *
 * 接受本地文件 → 转 base64（data:URL）→ 提交时由后端 imageInputResolver 上传到 storage
 * 也可以从图片库选择（resultUrl 形式）
 * 单张文件大小上限 10MB
 */

import { useRef, useState } from 'react';
import { Plus, X, FolderOpen } from 'lucide-react';
import { ImageLibraryPicker } from './ImageLibraryPicker';
import { deriveThumbnailUrl } from '../../../utils/imageDisplay';
import { withAuthToken } from '../../../utils/storageUrl';

interface VideoReferenceUploaderProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxCount: number;
  maxSizeMB?: number;
}

const DEFAULT_MAX_SIZE_MB = 10;

export function VideoReferenceUploader({
  images,
  onImagesChange,
  maxCount,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
}: VideoReferenceUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const remaining = Math.max(0, maxCount - images.length);

  const handlePickFromLibrary = (urls: string[]) => {
    const next = [...images];
    for (const url of urls) {
      if (next.length >= maxCount) break;
      next.push(url);
    }
    onImagesChange(next);
  };

  const handleAdd = async (files: FileList | null) => {
    if (!files) return;
    const next: string[] = [...images];
    for (const file of Array.from(files)) {
      if (next.length >= maxCount) break;
      if (file.size > maxSizeMB * 1024 * 1024) {
        alert(`图片 ${file.name} 超过 ${maxSizeMB}MB`);
        continue;
      }
      const dataUrl = await readAsDataUrl(file);
      next.push(dataUrl);
    }
    onImagesChange(next);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-light-text">参考图</span>
        <span className="text-xs text-light-muted">
          {images.length} / {maxCount}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {images.map((src, index) => (
          <div
            key={index}
            className="relative aspect-square rounded-xl overflow-hidden border border-light-border bg-light-hover"
          >
            <img src={renderImageSrc(src)} alt={`参考图 ${index + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {images.length < maxCount && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-light-border bg-white hover:border-accent-blue hover:text-accent-blue transition-all flex flex-col items-center justify-center gap-1 text-light-muted"
            >
              <Plus className="w-5 h-5" />
              <span className="text-xs">上传图片</span>
            </button>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="aspect-square rounded-xl border-2 border-dashed border-light-border bg-white hover:border-accent-blue hover:text-accent-blue transition-all flex flex-col items-center justify-center gap-1 text-light-muted"
            >
              <FolderOpen className="w-5 h-5" />
              <span className="text-xs">从图库选</span>
            </button>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleAdd(e.target.files)}
        className="hidden"
      />
      <p className="text-xs text-light-muted mt-1.5">
        单张 ≤ {maxSizeMB}MB，最多 {maxCount} 张；支持 jpg / png / webp
      </p>

      <ImageLibraryPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickFromLibrary}
        maxSelect={remaining > 0 ? remaining : 1}
        title="选择参考图"
      />
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('读取失败'));
    reader.readAsDataURL(file);
  });
}

function renderImageSrc(src: string): string {
  const previewSrc = deriveThumbnailUrl(src, 'small') || src;
  if (previewSrc.startsWith('data:')) return previewSrc;
  if (previewSrc.includes('/api/')) return withAuthToken(previewSrc);
  return previewSrc;
}
